import {
  AppModal,
  Button,
  Form,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useDuplicateMatterTemplate } from "@/lib/data/react-query/matters/matter-template";
import { MatterTemplateSchema } from "@/lib/data/schemas/matters/MatterSchema";
import type { MatterTemplate } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

interface DuplicateTemplateModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sourceTemplate: MatterTemplate;
  existingTemplateNames: string[];
  onDuplicated?: (template: MatterTemplate) => void;
}

const formSchema = MatterTemplateSchema.pick({
  name: true,
});

const defaultValues: z.infer<typeof formSchema> = {
  name: "",
};

export function DuplicateTemplateModal({
  open,
  setOpen,
  sourceTemplate,
  existingTemplateNames,
  onDuplicated,
}: DuplicateTemplateModalProps) {
  const formId = "duplicate-template-form";
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: "onSubmit",
  });
  const duplicateMatterTemplate = useDuplicateMatterTemplate();
  const fileExtension = getFileNameParts(sourceTemplate.name).fileExtension;

  const handleDuplicate = async (data: z.infer<typeof formSchema>) => {
    const fullFileName = fileExtension
      ? `${data.name}.${fileExtension}`
      : data.name;

    const normalizedExistingNames = new Set(
      existingTemplateNames.map((name) => name.toLowerCase()),
    );
    if (normalizedExistingNames.has(fullFileName.toLowerCase())) {
      form.setError("name", {
        type: "manual",
        message: "A template with this name already exists",
      });
      return;
    }

    try {
      const response = await duplicateMatterTemplate.mutateAsync({
        sourceTemplate,
        name: fullFileName,
      });
      setOpen(false);
      onDuplicated?.(response.data);
    } catch (error) {
      console.error("Failed to duplicate template", error);
    }
  };

  useEffect(() => {
    if (!open) {
      form.reset(defaultValues);
      return;
    }

    const baseName = getFileNameParts(sourceTemplate.name).fileName;
    form.reset({
      name: baseName ? `${baseName} copy` : "Copy",
    });
  }, [form, open, sourceTemplate]);

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title="Duplicate template"
      description="Choose a name for the duplicated template."
      isDirty={form.formState.isDirty}
      loading={duplicateMatterTemplate.isPending}
      size="lg"
      footer={
        <div className="flex w-full justify-end gap-2">
          <FormCancelButton
            setOpen={setOpen}
            isDirty={form.formState.isDirty}
          />
          <Button
            type="submit"
            form={formId}
            disabled={duplicateMatterTemplate.isPending}
          >
            Duplicate
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          id={formId}
          className="relative flex grow flex-col overflow-hidden"
          onSubmit={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await form.handleSubmit(handleDuplicate)();
          }}
        >
          {duplicateMatterTemplate.isPending ? <FormLoadingOverlay /> : null}
          <div className="overflow-y-auto">
            <fieldset
              className={cn(
                slideoverFieldsetClassName,
                "grid-cols-1 md:grid-cols-2",
              )}
            >
              <TextFormField
                register={form.register("name")}
                label="File name"
                placeholder="Enter a file name"
              />
              {fileExtension ? (
                <Typography variant="subHeading" className="mb-3 self-end">
                  .{fileExtension}
                </Typography>
              ) : (
                <div />
              )}
            </fieldset>
          </div>
        </form>
      </Form>
    </AppModal>
  );
}
